
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Index from "@/pages/Index";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Auth from "@/pages/Auth";
import CreateEvent from "@/pages/CreateEvent";
import EventOverview from "@/pages/EventOverview";
import SeatingPlan from "@/pages/SeatingPlan";
import OrderConfirmation from "@/pages/OrderConfirmation";
import PastEvents from "@/pages/PastEvents";
import PrideEventDirectory from "@/pages/PrideEventDirectory";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ProducerPage from "@/pages/ProducerPage";
import { QRPayment } from "@/pages/QRPayment";
import ProducerLogin from "@/pages/producers/Login";
import ProducerDashboard from "@/pages/producers/Dashboard";
import ProducerApply from "@/pages/producers/Apply";
import ProducerScanner from "@/pages/producers/Scanner";
import ProducerCompTickets from "@/pages/producers/CompTickets";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import { Toaster } from "@/components/ui/toaster";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { toast } from "sonner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      meta: {
        onError: (error: Error) => {
          console.error('Query error:', error);
          toast.error("An error occurred while loading data. Please try again.");
        }
      }
    },
  },
});

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white p-4">
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold">Something went wrong</h2>
        <pre className="text-sm bg-red-900/20 p-4 rounded-lg overflow-auto max-w-lg">
          {error.message}
        </pre>
        <button
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Router>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/create-event" element={<CreateEvent />} />
            <Route path="/events/:seoUrl" element={<EventOverview />} />
            <Route path="/events/:seoUrl/ticketing" element={<SeatingPlan />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/past-events" element={<PastEvents />} />
            <Route path="/pride-directory" element={<PrideEventDirectory />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/producers/:slug" element={<ProducerPage />} />
            <Route path="/producers/login" element={<ProducerLogin />} />
            <Route path="/producers/dashboard" element={<ProducerDashboard />} />
            <Route path="/producers/apply" element={<ProducerApply />} />
            <Route path="/producers/scanner" element={<ProducerScanner />} />
            <Route path="/producers/comp-tickets" element={<ProducerCompTickets />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/qr-payment" element={<QRPayment />} />
          </Routes>
          <Toaster />
        </Router>
        <Analytics />
        <SpeedInsights />
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
